import { useState } from "react"
import * as yup from "yup"
import { Form, Formik } from "formik"
import { makeStyles } from "tss-react/mui"
import { Alert, Link, Stack, Typography } from "@mui/material"
import { isPossiblePhoneNumber } from "libphonenumber-js"
import { Player } from "@lottiefiles/react-lottie-player"
import { useSearchParams } from "react-router-dom"
import {
    CardSubscriptionCompaniesResponse,
    IdCardSignupFormValues,
    IdCardSubscriptionUser,
    TransformedCompany,
} from "../loginTypes"
import { authenticateByActivateIdCard, fetchCardSubscriptionCompanies, LoginQuery } from "../loginApi"
import { IdCardSignupLoadingState } from "../components/idCardSignup/IdCardSignupLoadingState"
import { prepareInitialValues, transformCompanies } from "../components/idCardSignup/helpers"
import { IdCardSignupFormContent } from "../components/idCardSignup/IdCardSignupFormContent"
import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import loadingAnimationSuccess from "../assets/success-lottie-anim.json"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { AppError, ErrorDisplay } from "../../../core/api/errorTypes"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHandPointRight } from "@fortawesome/pro-light-svg-icons"
import { useAppSelector } from "../../store/storeHooks"
import { selectSelectedLocale } from "../../localization/localizationSelectors"
import { CompleteRegistrationHeader } from "../components/idCardSignup/IdCardSignupStepHeader"

const useStyles = makeStyles()(() => ({
    animation: {
        height: "8rem",
        marginBottom: "2rem",
    },
    errorWrapper: {
        margin: "1rem 0",
    },
    linkIcon: {
        color: "black",
        marginRight: "0.3rem",
    },
    link: {
        paddingTop: "0.5rem",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
}))

const MAX_NAME_LENGTH = 50

export function useIdCardsParams() {
    const [searchParams] = useSearchParams()

    const activationToken = searchParams.get("uid")
    const authToken = searchParams.get("otp")
    const isUserExists = (searchParams.get("userExists") || "false") === "true"

    let errorMessage = undefined

    if (!activationToken) {
        errorMessage = `Link was missing information: activationToken`
        throw new AppError("E_INVALID_LINK", "Invalid link", errorMessage)
    }

    if (!authToken) {
        errorMessage = `Link was missing information: authToken`
        throw new AppError("E_INVALID_LINK", "Invalid link", errorMessage)
    }

    return {
        activationToken,
        authToken,
        isUserExists,
    }
}

const transformCardSubscriptionCompanyData = ({ companies, user }: CardSubscriptionCompaniesResponse) => {
    if (!companies) return { companies: [], user: undefined }

    return { companies: transformCompanies(companies), user }
}

export const IdCardSignup = () => {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const selectedLocale = useAppSelector(selectSelectedLocale)

    const howToUseFacilitynetPaymentSolutionUrl =
        selectedLocale === "da-DK"
            ? "https://support.facilitynet.dk/hc/da/articles/26645638850450"
            : "https://support.facilitynet.dk/hc/en-dk/articles/26645638850450"

    const [isComplete, setIsComplete] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const { activationToken, authToken, isUserExists } = useIdCardsParams()

    const {
        data = { companies: [], user: undefined },
        isLoading,
        error: companiesError,
        isError,
    } = useApiQuery(fetchCardSubscriptionCompanies, {
        queryName: LoginQuery.ID_CARD_SUBSCRIPTION_COMPANIES,
        dependencies: {
            activationToken,
        },
        errorDisplay: ErrorDisplay.NONE,
        select: transformCardSubscriptionCompanyData,
    })

    const { mutateAsync: callAuthenticateByActivateIdCard } = usePublicApiMutation(authenticateByActivateIdCard, {
        async onSuccess() {
            setIsComplete(true)
            setErrorMessage(null)
        },
        async onError(_, error) {
            console.error("Error activating ID card:", error)
            if (error && typeof error === "object" && "displayMessage" in error) {
                setErrorMessage(String(error.displayMessage))
            } else {
                setErrorMessage(l10n.getString("id-card-activation-generic-error"))
            }
        },
    })

    const onSubmit = async ({
        subscriptionId,
        displayName,
        mobileNumber,
        userGroupId,
        companyId,
    }: IdCardSignupFormValues) => {
        setErrorMessage(null)
        const selectedCompany = data.companies.find((company) => company?.id === companyId)

        const requestBody = {
            cardSubscription: {
                activationToken,
                subscriptionId: subscriptionId!,
            },
            authentication: {
                type: "ONE_TIME_TOKEN",
                token: authToken,
            },
            ...(isUserExists
                ? {
                      user: {
                          uid: data && data.user ? data.user.uid : "",
                      },
                  }
                : {
                      user: {
                          displayName,
                          ...(!!mobileNumber ? { mobileNumber } : {}),
                          userGroup: {
                              id: userGroupId ? +userGroupId : 0,
                          },
                          company: {
                              id: selectedCompany?.companyId || 0,
                          },
                      },
                  }),
        }

        await callAuthenticateByActivateIdCard(requestBody)
    }

    const renderCompaniesError = () => {
        if (companiesError) {
            return (
                <Alert severity="error" className={classes.errorWrapper}>
                    {companiesError instanceof AppError
                        ? companiesError?.displayMessage
                        : l10n.getString("id-card-signup-companies-error")}
                </Alert>
            )
        }
        return null
    }

    return (
        <>
            {!isComplete ? (
                <>
                    {isLoading || (!data.companies.length && !isError) ? (
                        <IdCardSignupLoadingState />
                    ) : isError ? (
                        <>
                            <CompleteRegistrationHeader />
                            {renderCompaniesError()}
                        </>
                    ) : (
                        <IdCardSignupForm
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                            onSubmit={onSubmit}
                            isUserExists={isUserExists}
                            existingUser={data?.user}
                            initialValues={prepareInitialValues(data.companies)}
                            activationToken={activationToken}
                            companiesList={data.companies}
                        />
                    )}
                </>
            ) : (
                <Stack alignItems="center">
                    <Player
                        keepLastFrame
                        src={loadingAnimationSuccess}
                        autoplay
                        speed={1}
                        className={classes.animation}
                    />
                    <LocalizedStrict id="id-card-signup-complete">
                        <Typography variant="h4" fontWeight={600} marginBottom="2rem" textAlign="center">
                            Congratulation
                        </Typography>
                    </LocalizedStrict>

                    <LocalizedStrict id="id-card-signup-complete-description">
                        <Typography maxWidth="90%" marginBottom="5rem" variant="h6" textAlign="center">
                            Your card has now been subscribed and you can start using it.
                        </Typography>
                    </LocalizedStrict>

                    <Stack>
                        <LocalizedStrict id="id-card-signup-complete-learn-more">
                            <Typography color="#1976d2" variant="subtitle1" textAlign="center">
                                Learn more:
                            </Typography>
                        </LocalizedStrict>

                        <Link className={classes.link} href={howToUseFacilitynetPaymentSolutionUrl} target="_blank">
                            <FontAwesomeIcon className={classes.linkIcon} icon={faHandPointRight} />
                            <LocalizedStrict id="id-card-signup-complete-learn-more-link">
                                <span>How to I use Facilitynet payment solution?</span>
                            </LocalizedStrict>
                        </Link>
                    </Stack>
                </Stack>
            )}
        </>
    )
}

type IdCardSignupFormProps = Readonly<{
    initialValues: IdCardSignupFormValues
    onSubmit: (values: IdCardSignupFormValues) => void
    activationToken: string
    companiesList: TransformedCompany[]
    isUserExists: boolean
    existingUser?: IdCardSubscriptionUser
    errorMessage?: string | null
    setErrorMessage: (errorMessage: string | null) => void
}>

const IdCardSignupForm = ({
    onSubmit,
    initialValues,
    activationToken,
    companiesList,
    isUserExists,
    existingUser,
    errorMessage,
    setErrorMessage,
}: IdCardSignupFormProps) => {
    const { l10n } = useAppLocalization()

    const validationSchema = yup.object({
        displayName: isUserExists
            ? yup.string().notRequired()
            : yup
                  .string()
                  .required(l10n.getString("required-field"))
                  .max(MAX_NAME_LENGTH, l10n.getString("id-card-signup-field-name-max-length")),
        subscriptionId: yup.number().required(l10n.getString("required-field")),
        mobileNumber: isUserExists
            ? yup.string().notRequired()
            : yup.string().test({
                  name: "is-valid-phonenumber",
                  test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                  message: l10n.getString("id-card-signup-field-mobile-number-invalid"),
              }),
        companyId: yup.string().required(l10n.getString("required-field")),
        userGroupId: yup.string().required(l10n.getString("required-field")),
    })

    return (
        <>
            <Formik<IdCardSignupFormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                // validateOnBlur={false}
            >
                <Form>
                    <IdCardSignupFormContent
                        setErrorMessage={setErrorMessage}
                        errorMessage={errorMessage}
                        existingUser={existingUser}
                        isUserExists={isUserExists}
                        activationToken={activationToken}
                        companiesList={companiesList}
                    />
                </Form>
            </Formik>
        </>
    )
}
